'use client'

import React, { useCallback, useMemo, useState } from 'react'
import {
  cartPickupStoreSelector,
  formatPriceToNumber,
  resolveCatchMessage,
  TBaseComponentProps,
  TCatchMessage,
  useAddProductAlertMutation,
  useCart,
  useCreateTestRideMutation,
  useLazyGetProductAlertQuery,
  useLoadingContext,
  useMediaQuery,
  userIsLoggedInSelector,
  useToastContext,
  useWishlist,
} from '@segway/core'
import { AffirmDataParsed } from '@segway/core/src/businessHooks/useProductAffirmData'
import {
  BundleProduct,
  CreateTestRideMutationVariables,
  GetProductsQuery,
  StoreResult,
} from '@segway/core/src/graphql/generated/graphql'
import { useAppSelector } from '@segway/core/src/store/hooks'
import Big from 'big.js'
import { createContext, useContextSelector } from 'use-context-selector'

import { useRouter } from '@/i18n/navigation'
import reportKlaviyoEvent from '@/utils/klaviyo'

type Products = NonNullable<GetProductsQuery['products']>
type ProductItems = NonNullable<Products['items']>
export type ProductItem = ProductItems[number]

export type BundleProducts = {
  id: number
  value: number
  qty: number
  sku: string
  name: string
  images: string[]
  small_image: string
  price: number
  sub_name: string
  isSaleable: boolean
  description: string
  is_default: boolean
  url_key: string
  categories: { name: string; url_path: string }[]
  gtm_item_categories: string | null
}

type TProductContextValue = {
  product: ProductItem // 产品信息
  cartStore: StoreResult | null // 购物车 pickup 地址
  quantity: number // 选择数量
  isWishlist: boolean // 是否已收藏
  isSubscribe: boolean // 是否订阅商品
  isInStock: boolean // 是否在售
  isBundle: boolean // 是否bundle产品
  isEbike: boolean // 是否Ebike产品
  isNoBundleAndOptions: boolean //是否没有bundle产品和selectOptions产品
  selectedBundle: BundleProducts[] //选中 bundle
  totalPrice: number // 总价
  totalPriceOrigin: number // 原价
  savePrice: number // 优惠价
  responsive: Record<string, boolean> | undefined // 响应式
  productAffirmData: AffirmDataParsed | null
  setProduct: React.Dispatch<React.SetStateAction<ProductItem>>
  setSelectedStore: React.Dispatch<React.SetStateAction<StoreResult | null>>
  setQuantity: (quantity: number) => void
  setSelectedBundle: React.Dispatch<React.SetStateAction<BundleProducts[]>>
  addToCart: (type?: boolean) => Promise<boolean>
  handleChangeStoreOnCart: (storeId: number, isHomeDelivery: boolean) => Promise<boolean>
  getStoreAddress: (store: StoreResult) => string
  createTestRide: (input: CreateTestRideMutationVariables) => Promise<void>
  checkInWishlist: (sku: string) => void
  checkIsSubscribe: (id: number) => void
  addProductAlert: (id: number) => void
  toggleWishlist: (sku: string) => void
  productInPartsCategory: boolean // 当前产品是否在 parts category 分类中
}

type TProductProvider = TBaseComponentProps

const ProductContext = createContext<TProductContextValue>({
  product: null,
  cartStore: null,
  quantity: 1,
  isWishlist: false,
  isSubscribe: false,
  isInStock: false,
  isBundle: false,
  isNoBundleAndOptions: false,
  isEbike: false,
  selectedBundle: [],
  totalPrice: 0,
  totalPriceOrigin: 0,
  savePrice: 0,
  responsive: undefined,
  productAffirmData: null,
  setProduct: () => {},
  setSelectedStore: () => {},
  setQuantity: () => {},
  setSelectedBundle: () => {},
  addToCart: async () => false,
  handleChangeStoreOnCart: async () => false,
  getStoreAddress: () => '',
  createTestRide: async () => {},
  checkInWishlist: () => {},
  checkIsSubscribe: () => {},
  toggleWishlist: () => {},
  addProductAlert: () => {},
  productInPartsCategory: false,
})

const ProductProvider = (props: TProductProvider) => {
  const { children } = props
  const toast = useToastContext()
  const loading = useLoadingContext()
  const responsive = useMediaQuery()
  const router = useRouter()

  const { addSimpleProductsToCart, addBundleProductsToCart, refreshCart, setPickupStoreOnCart } =
    useCart()
  const { addSkuToWishlist, removeSkuFromWishlist, inWishlistBySku } = useWishlist()

  const cartStore = useAppSelector(cartPickupStoreSelector)
  const isLoggedIn = useAppSelector(userIsLoggedInSelector)

  const [product, setProduct] = useState<ProductItem>(null)
  const [selectedStore, setSelectedStore] = useState<StoreResult | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [selectedBundle, setSelectedBundle] = useState<BundleProducts[]>([])
  const [isWishlist, setIsWishlist] = useState(false)
  const [isSubscribe, setIsSubscribe] = useState(false)

  const [createTestRideMutation] = useCreateTestRideMutation()
  const [addProductAlertMutation] = useAddProductAlertMutation()
  const [getProductAlertQuery] = useLazyGetProductAlertQuery()

  /**
   * 是否bundle产品
   */
  const isBundle = useMemo(() => {
    return product?.type_id === 'bundle'
  }, [product])

  /**
   * 是否在售
   */
  const isInStock = useMemo(() => {
    if (isBundle) {
      return product?.saleable_message?.is_saleable || false
    } else {
      return product?.saleable_message?.is_saleable || false
    }
  }, [product, isBundle])

  /**
   * 是否没有bundle产品和selectOptions产品
   */
  const isNoBundleAndOptions = useMemo(() => {
    if (product?.type_id !== 'bundle') {
      return true
    }

    let flag = false

    ;(product as BundleProduct).items?.forEach((item) => {
      if (item?.options && !item.required) {
        flag = true
      }
    })

    return !(flag && isInStock) && !product?.select_option_products?.length
  }, [isInStock, product])

  /**
   * 是否Ebike产品
   */
  const isEbike = useMemo(() => {
    return product?.saleable_message?.is_ebike || false
  }, [product])

  /**
   * 总价
   */
  const totalPrice = useMemo(() => {
    let price = new Big(0)
    if (product) {
      const finalPrice = product.price_range?.minimum_price?.final_price?.value || 0
      price = Big(finalPrice).times(quantity)
    }
    if (selectedBundle.length > 0) {
      selectedBundle.forEach((item) => {
        const finalPrice = item.price || 0
        price = price.plus(Big(finalPrice).times(quantity))
      })
    }
    return Number(price)
  }, [product, selectedBundle, quantity])

  /**
   * 总价(原价)
   */
  const totalPriceOrigin = useMemo(() => {
    let price = new Big(0)
    if (product) {
      const finalPrice = product.price_range?.minimum_price?.regular_price.value || 0
      price = Big(finalPrice).times(quantity)
    }
    if (selectedBundle.length > 0) {
      selectedBundle.forEach((item) => {
        const finalPrice = item.price || 0
        price = price.plus(Big(finalPrice).times(quantity))
      })
    }
    return Number(price)
  }, [product, selectedBundle, quantity])

  /**
   * Affirm data
   */
  const productAffirmData = useMemo(() => {
    return product?.affirm_data ? JSON.parse(product?.affirm_data) : null
  }, [product])

  /**
   *  Save price
   */
  const savePrice = useMemo(() => {
    let price = new Big(0)
    if (product) {
      const finalPrice = product.price_range?.minimum_price?.discount?.amount_off || 0
      price = Big(finalPrice).times(quantity)
    }
    return Number(price)
  }, [product, quantity])

  /**
   * 获取门店地址
   */
  const getStoreAddress = useCallback((store: StoreResult) => {
    const region = store.region?.code || store.region?.name
    const formattedAddress = [
      store.address,
      store.city,
      region + ' ' + store.zipcode,
      store.country_id,
    ]
      .filter((item) => {
        return !!item
      })
      .join(', ')

    return formattedAddress
  }, [])

  /**
   * simple 产品加购参数
   */
  const addSimpleProductsParams = useCallback(
    (type: boolean) => {
      const params = {
        cart_items: [{ data: { sku: product?.sku || '', quantity } }],
      }
      if (isEbike) {
        Object.assign(params, {
          pickup_store: selectedStore?.id,
          home_delivery: type,
        })
      }
      return params
    },
    [isEbike, selectedStore, product?.sku, quantity],
  )

  /**
   * simple 产品加购
   */
  const handleAddSimpleProductsToCart = useCallback(
    async (type = false) => {
      loading.show()
      const result = await addSimpleProductsToCart(addSimpleProductsParams(type))
      if (result) {
        setTimeout(() => {
          let allProducts: object[] = []
          let bundleProducts: object[] = []
          let urlKeyFullLink = ''

          if (product?.categories && product?.categories.length) {
            const urlPath = product.categories[product.categories.length - 1]?.url_path

            if (urlPath) {
              urlKeyFullLink = urlPath.replace(/\//g, '_')
            }
          }

          if (selectedBundle.length) {
            bundleProducts = selectedBundle.map((item) => {
              let urlKeyFullLink = ''

              if (item?.categories && item?.categories.length) {
                const urlPath = item.categories[item.categories.length - 1]?.url_path

                if (urlPath) {
                  urlKeyFullLink = urlPath.replace(/\//g, '_')
                }
              }

              return {
                ProductID: item.id,
                Name: item.name,
                SKU: item.sku,
                URL: window.location.origin + item.url_key,
                Price: formatPriceToNumber(item.price || 0).numberValue,
                FinalPrice: formatPriceToNumber(item.price || 0).numberValue,
                Categories: urlKeyFullLink,
              }
            })
          }

          allProducts = [
            {
              ProductID: product?.id,
              Name: product?.name,
              SKU: product?.sku,
              URL: window.location.origin + product?.url_key,
              Price: formatPriceToNumber(
                product?.price_range.minimum_price.regular_price.value || 0,
              ).numberValue,
              FinalPrice: formatPriceToNumber(
                product?.price_range.minimum_price.final_price.value || 0,
              ).numberValue,
              Categories: urlKeyFullLink,
            },
            ...bundleProducts,
          ]

          const allItemsName = allProducts.map((item) => (item as Record<string, string>)['Name'])

          reportKlaviyoEvent([
            'track',
            'Added To Cart',
            {
              $value: formatPriceToNumber(result.prices?.grand_total?.value ?? 0).numberValue,
              AddedItemProductName: product?.name,
              AddedItemProductID: product?.id,
              AddedItemSKU: product?.sku,
              AddedItemImageURL: product?.image?.url,
              AddedItemURL: window.location.href,
              AddedItemPrice: formatPriceToNumber(totalPrice).numberValue,
              AddedItemQuantity: quantity,
              ItemNames: allItemsName,
              CheckoutURL: window.location.origin + '/checkout',
              Items: allProducts,
            },
          ])
        }, 1000)
        await refreshCart()
      }
      loading.hide()
      return !!result
    },
    [
      loading,
      addSimpleProductsToCart,
      addSimpleProductsParams,
      refreshCart,
      product,
      selectedBundle,
      totalPrice,
      quantity,
    ],
  )

  // 主品参数
  const mainProductParam = useMemo(() => {
    if (isBundle && product) {
      const mainProduct = (product as BundleProduct).items?.find((i) => i?.required)

      if (mainProduct?.option_id && mainProduct?.options?.[0]?.id) {
        return {
          id: mainProduct?.option_id,
          quantity: quantity,
          value: [String(mainProduct?.options?.[0]?.id)],
        }
      }
      return null
    }
  }, [isBundle, product, quantity])

  /**
   * bundle 产品加购参数
   */
  const addBundleProductsParams = useCallback(
    (type: boolean) => {
      // const options = selectedBundle.map((item) => ({
      //   id: item.id,
      //   quantity: item.qty,
      //   value: [String(item.value)],
      // }))

      // bundle 产品 checkbox 选项
      const options = selectedBundle.reduce<{ id: number; quantity: number; value: string[] }[]>(
        (result, option) => {
          const existingItemIndex = result.findIndex((item) => item.id === option.id)

          if (existingItemIndex > -1) {
            result[existingItemIndex].value = [
              ...result[existingItemIndex].value,
              String(option.value),
            ]
          } else {
            result.push({
              id: option.id,
              quantity: quantity,
              value: [String(option.value)],
            })
          }

          return result
        },
        [],
      )

      const params = {
        cart_items: [
          {
            data: { sku: product?.sku || '', quantity },
            bundle_options: mainProductParam ? [mainProductParam, ...options] : options,
          },
        ],
      }
      if (isEbike) {
        Object.assign(params, {
          pickup_store: selectedStore?.id,
          home_delivery: type,
        })
      }
      return params
    },
    [isEbike, selectedStore, mainProductParam, selectedBundle, product?.sku, quantity],
  )

  /**
   * bundle 产品加购
   */
  const handleAddBundleProductsToCart = useCallback(
    async (type = false) => {
      loading.show()
      const result = await addBundleProductsToCart(addBundleProductsParams(type))
      if (result) {
        setTimeout(() => {
          let allProducts: object[] = []
          let bundleProducts: object[] = []
          let urlKeyFullLink = ''

          if (product?.categories && product?.categories.length) {
            const urlPath = product.categories[product.categories.length - 1]?.url_path

            if (urlPath) {
              urlKeyFullLink = urlPath.replace(/\//g, '_')
            }
          }

          if (selectedBundle.length) {
            bundleProducts = selectedBundle.map((item) => {
              let urlKeyFullLink = ''

              if (item?.categories && item?.categories.length) {
                const urlPath = item.categories[item.categories.length - 1]?.url_path

                if (urlPath) {
                  urlKeyFullLink = urlPath.replace(/\//g, '_')
                }
              }

              return {
                ProductID: item.id,
                Name: item.name,
                SKU: item.sku,
                URL: window.location.origin + item.url_key,
                Price: formatPriceToNumber(item.price || 0).numberValue,
                FinalPrice: formatPriceToNumber(item.price || 0).numberValue,
                Categories: urlKeyFullLink,
              }
            })
          }

          allProducts = [
            {
              ProductID: product?.id,
              Name: product?.name,
              SKU: product?.sku,
              URL: window.location.origin + product?.url_key,
              Price: formatPriceToNumber(
                product?.price_range.minimum_price.regular_price.value || 0,
              ).numberValue,
              FinalPrice: formatPriceToNumber(
                product?.price_range.minimum_price.final_price.value || 0,
              ).numberValue,
              Categories: urlKeyFullLink,
            },
            ...bundleProducts,
          ]

          const allItemsName = allProducts.map((item) => (item as Record<string, string>)['Name'])

          reportKlaviyoEvent([
            'track',
            'Added To Cart',
            {
              $value: formatPriceToNumber(result.prices?.grand_total?.value ?? 0).numberValue,
              AddedItemProductName: product?.name,
              AddedItemProductID: product?.id,
              AddedItemSKU: product?.sku,
              AddedItemImageURL: product?.image?.url,
              AddedItemURL: window.location.href,
              AddedItemPrice: formatPriceToNumber(totalPrice).numberValue,
              AddedItemQuantity: quantity,
              ItemNames: allItemsName,
              CheckoutURL: window.location.origin + '/checkout',
              Items: allProducts,
            },
          ])
        }, 1000)
        await refreshCart()
      }
      loading.hide()
      return !!result
    },
    [
      loading,
      addBundleProductsToCart,
      addBundleProductsParams,
      refreshCart,
      product,
      selectedBundle,
      totalPrice,
      quantity,
    ],
  )

  /**
   * 添加购物车
   */
  const addToCart = useCallback(
    async (type = false) => {
      let result
      if (isBundle) {
        result = await handleAddBundleProductsToCart(type)
      } else {
        result = await handleAddSimpleProductsToCart(type)
      }

      return result
    },
    [handleAddBundleProductsToCart, handleAddSimpleProductsToCart, isBundle],
  )

  /**
   * 修改门店
   */
  const handleChangeStoreOnCart = useCallback(
    async (storeId: number, isHomeDelivery: boolean) => {
      loading.show()
      const result = await setPickupStoreOnCart({
        homeDelivery: isHomeDelivery,
        store_id: storeId,
      })
      if (result) {
        await refreshCart()
        toast.show({
          icon: 'success',
          content: 'Change successfully',
        })
      }
      loading.hide()
      return result
    },
    [setPickupStoreOnCart, loading, toast, refreshCart],
  )

  /**
   * 预约试驾
   */
  const createTestRide = useCallback(
    async (input: CreateTestRideMutationVariables) => {
      try {
        loading.show()
        const response = await createTestRideMutation(input).unwrap()
        if (response.createRideTest?.store_id) {
          toast.show({
            icon: 'success',
            content: 'Book successfully',
          })
        }
      } catch (error) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(error as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [toast, createTestRideMutation, loading],
  )

  /**
   * 查询是否收藏
   */
  const checkInWishlist = useCallback(
    async (sku: string) => {
      const result = await inWishlistBySku(sku)
      setIsWishlist(!!result)
    },
    [inWishlistBySku],
  )

  /**
   * 设置/取消 wishlist
   */
  const toggleWishlist = useCallback(
    async (sku: string) => {
      if (!isLoggedIn) {
        router.push('/sign-in?callbackUrl=' + window.location.href)
        return
      }

      if (isWishlist) {
        loading.show()
        const result = await removeSkuFromWishlist({ skus: [sku] })
        if (result) {
          setIsWishlist(false)
        }
      } else {
        loading.show()
        const result = await addSkuToWishlist({
          wishlistItems: [
            {
              sku,
              quantity: 1,
            },
          ],
        })
        if (result) {
          setIsWishlist(true)
        }
      }
      loading.hide()
    },
    [addSkuToWishlist, loading, removeSkuFromWishlist, isWishlist, isLoggedIn, router],
  )

  /**
   * 查询是否订阅
   */
  const checkIsSubscribe = useCallback(
    async (id: number) => {
      try {
        const response = await getProductAlertQuery({
          productId: id,
        }).unwrap()
        setIsSubscribe(response?.getProductAlert || false)
      } catch (error) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(error as TCatchMessage),
        })
      }
    },
    [getProductAlertQuery, toast],
  )

  /**
   * 商品订阅
   */
  const addProductAlert = useCallback(
    async (id: number) => {
      if (!isLoggedIn) {
        router.push('/sign-in?callbackUrl=' + window.location.href)
        return
      }
      try {
        loading.show()
        const response = await addProductAlertMutation({
          productId: id,
        }).unwrap()
        setIsSubscribe(response?.addProductAlert?.result || false)
        if (response.addProductAlert?.result) {
          toast.show({
            icon: 'success',
            content: 'Suscripción exitosa',
          })
        }
      } catch (error) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(error as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [loading, addProductAlertMutation, isLoggedIn, router, toast],
  )

  /**
   * 当前产品是否在 parts category 分类中
   */
  const productInPartsCategory = useMemo(() => {
    if (product?.categories?.length) {
      if (product.categories[0]?.url_path) {
        return /parts/gi.test(product.categories[0]?.url_path)
      }
    }

    return false
  }, [product])

  const contextValue = useMemo(
    () => ({
      cartStore,
      product,
      quantity,
      isWishlist,
      isSubscribe,
      isInStock,
      isBundle,
      isNoBundleAndOptions,
      isEbike,
      selectedBundle,
      totalPrice,
      totalPriceOrigin,
      savePrice,
      responsive,
      productAffirmData,
      setProduct,
      setSelectedStore,
      setQuantity,
      setSelectedBundle,
      getStoreAddress,
      addToCart,
      handleChangeStoreOnCart,
      createTestRide,
      checkInWishlist,
      checkIsSubscribe,
      addProductAlert,
      toggleWishlist,
      productInPartsCategory,
    }),
    [
      cartStore,
      product,
      quantity,
      isWishlist,
      isSubscribe,
      isInStock,
      isBundle,
      isNoBundleAndOptions,
      isEbike,
      selectedBundle,
      totalPrice,
      totalPriceOrigin,
      savePrice,
      responsive,
      productAffirmData,
      getStoreAddress,
      addToCart,
      handleChangeStoreOnCart,
      createTestRide,
      checkInWishlist,
      checkIsSubscribe,
      addProductAlert,
      toggleWishlist,
      productInPartsCategory,
    ],
  )

  return <ProductContext.Provider value={contextValue}>{children}</ProductContext.Provider>
}

export const useProductData = () => useContextSelector(ProductContext, (ctx) => ctx?.product)

export const useProduct = () => {
  const cartStore = useContextSelector(ProductContext, (ctx) => ctx?.cartStore)
  const quantity = useContextSelector(ProductContext, (ctx) => ctx?.quantity)
  const isWishlist = useContextSelector(ProductContext, (ctx) => ctx?.isWishlist)
  const isSubscribe = useContextSelector(ProductContext, (ctx) => ctx?.isSubscribe)
  const isInStock = useContextSelector(ProductContext, (ctx) => ctx?.isInStock)
  const isBundle = useContextSelector(ProductContext, (ctx) => ctx?.isBundle)
  const isNoBundleAndOptions = useContextSelector(
    ProductContext,
    (ctx) => ctx?.isNoBundleAndOptions,
  )
  const isEbike = useContextSelector(ProductContext, (ctx) => ctx?.isEbike)
  const selectedBundle = useContextSelector(ProductContext, (ctx) => ctx?.selectedBundle)
  const totalPrice = useContextSelector(ProductContext, (ctx) => ctx?.totalPrice)
  const totalPriceOrigin = useContextSelector(ProductContext, (ctx) => ctx?.totalPriceOrigin)
  const savePrice = useContextSelector(ProductContext, (ctx) => ctx?.savePrice)
  const responsive = useContextSelector(ProductContext, (ctx) => ctx?.responsive)
  const productAffirmData = useContextSelector(ProductContext, (ctx) => ctx?.productAffirmData)
  const setProduct = useContextSelector(ProductContext, (ctx) => ctx?.setProduct)
  const setSelectedStore = useContextSelector(ProductContext, (ctx) => ctx?.setSelectedStore)
  const setQuantity = useContextSelector(ProductContext, (ctx) => ctx?.setQuantity)
  const setSelectedBundle = useContextSelector(ProductContext, (ctx) => ctx?.setSelectedBundle)
  const getStoreAddress = useContextSelector(ProductContext, (ctx) => ctx?.getStoreAddress)
  const addToCart = useContextSelector(ProductContext, (ctx) => ctx?.addToCart)
  const handleChangeStoreOnCart = useContextSelector(
    ProductContext,
    (ctx) => ctx?.handleChangeStoreOnCart,
  )
  const createTestRide = useContextSelector(ProductContext, (ctx) => ctx?.createTestRide)
  const checkInWishlist = useContextSelector(ProductContext, (ctx) => ctx?.checkInWishlist)
  const toggleWishlist = useContextSelector(ProductContext, (ctx) => ctx?.toggleWishlist)
  const checkIsSubscribe = useContextSelector(ProductContext, (ctx) => ctx?.checkIsSubscribe)
  const addProductAlert = useContextSelector(ProductContext, (ctx) => ctx?.addProductAlert)
  const productInPartsCategory = useContextSelector(
    ProductContext,
    (ctx) => ctx?.productInPartsCategory,
  )

  return {
    cartStore,
    quantity,
    isWishlist,
    isSubscribe,
    isInStock,
    isBundle,
    isEbike,
    isNoBundleAndOptions,
    selectedBundle,
    totalPrice,
    totalPriceOrigin,
    savePrice,
    responsive,
    productAffirmData,
    setProduct,
    setSelectedStore,
    setQuantity,
    setSelectedBundle,
    getStoreAddress,
    addToCart,
    handleChangeStoreOnCart,
    createTestRide,
    checkInWishlist,
    checkIsSubscribe,
    addProductAlert,
    toggleWishlist,
    productInPartsCategory,
  }
}

export default ProductProvider
